// 포털 화면 선택 — 같은 호스트에서 홈 화면을 두 벌로 두기 위한 소형 스토어.
//
// lib/navigation.ts 와 같은 방식이다: 값은 모듈 밖(여기서는 localStorage)에 두고,
// 바뀔 때 커스텀 이벤트를 쏘아 구독자에게 알린다. React 16.10 이라
// useSyncExternalStore 를 쓸 수 없어 useState + 이벤트로 맞춘다.
//
// 지금은 TopNav 의 토글만 이 값을 바꾼다. 나중에 부서(JWT 의 deptCode)로 자동 판정하게
// 바꾸더라도 읽는 쪽(Layout)은 그대로 두고 resolve 규칙만 여기서 고치면 된다.
import { useEffect, useState } from 'react';

/** 포털 종류 — 늘어나면 여기에 추가한다 */
export type PortalId = 'default' | 'zeirishi';

export const PORTAL_LABEL: Record<PortalId, string> = {
  default: '기본 포털',
  zeirishi: '세리사무소 포털',
};

const STORAGE_KEY = 'portalId';
/** 포털 전환을 알리는 커스텀 이벤트명 */
const PORTAL_EVENT = 'hostPortalChange';

function isPortalId(value: string | null): value is PortalId {
  return value === 'default' || value === 'zeirishi';
}

/** 저장된 선택을 읽는다 — 값이 없거나 모르는 값이면 기본 포털 */
function read(): PortalId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isPortalId(stored) ? stored : 'default';
  } catch {
    // 사파리 프라이빗 등에서 localStorage 접근 자체가 막힐 수 있다 — 기본값으로 산다
    return 'default';
  }
}

/** 포털 전환 — 저장하고 구독자(usePortal)에게 알린다 */
export function setPortal(id: PortalId): void {
  if (read() === id) return;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // 저장에 실패해도 이번 세션 동안은 바뀐 화면을 보여준다
  }
  window.dispatchEvent(new Event(PORTAL_EVENT));
}

/** 현재 포털을 상태로 구독 — 어느 컴포넌트에서 토글하든 함께 갱신된다 */
export function usePortal(): PortalId {
  const [portal, setPortalState] = useState<PortalId>(read);

  useEffect(() => {
    const sync = () => setPortalState(read());
    window.addEventListener(PORTAL_EVENT, sync);
    // 다른 탭에서 전환했을 때도 따라간다 (storage 이벤트는 같은 탭에서는 안 온다)
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PORTAL_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return portal;
}
